import { Button, Card, Empty, Flex, Form, InputNumber, Modal, Select, Spin, Tag } from "antd";
import { getAvailableVTUserIds, getDialedNumbers } from "api/services/ContactService";
import { TVTDialedNumberMap } from "api/services/models/types";
import { AxiosError } from "axios";
import { Header } from "components/common/Header";
import ErrorViewer from "components/ErrorViewer";
import { useEffect, useState } from "react";

type TSearchAvailableVTUsersProps = {
  onClose: (userId?: string, dialedNumber?: string) => void;
};

type TSearchAvailableVTUsers = {
  startUserId: number;
  endUserId: number;
  dialedNumber: string;
};

const SearchAvailableVTUsers = ({ onClose }: Readonly<TSearchAvailableVTUsersProps>) => {
  const [searchForm] = Form.useForm();
  const [availableUserIds, setAvailableUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [dialedNumbersMap, setDialedNumbersMap] = useState<TVTDialedNumberMap[]>();
  const [selectedDialedNumber, setSelectedDialedNumber] = useState<string>();
  const [lookedup, setLookedup] = useState(false);

  useEffect(() => {
    setLoading(true);
    getDialedNumbers()
      .then(d => {
        setDialedNumbersMap(d);
        searchForm.setFieldValue("dialedNumber", d.at(0)?.dialedNumber);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  const onSearchFinish = (values: TSearchAvailableVTUsers) => {
    setLoading(true);
    setError(null);
    setLookedup(true);
    setSelectedDialedNumber(values.dialedNumber);
    getAvailableVTUserIds(values.startUserId, values.endUserId, values.dialedNumber)
      .then(setAvailableUserIds)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={<Header header="Search Available VT Users" onClose={onClose} />}
      open={true}
      footer={null}
      closeIcon={null}
      onCancel={() => onClose()}
      destroyOnClose={true}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}

      {error && <ErrorViewer error={error} />}

      <Card
        className="default-border"
        styles={{ body: { padding: "8px" } }}
        style={{ marginBottom: "8px" }}>
        <Form
          form={searchForm}
          name="search_vtuser_id"
          onFinish={onSearchFinish}
          layout="inline"
          size="small"
          colon={false}
          validateTrigger={["onChange", "onBlur"]}
          clearOnDestroy={true}
          autoComplete="off">
          <Form.Item
            rules={[{ required: true, message: "required" }]}
            key={"startUserId"}
            name={"startUserId"}
            label="Start Id">
            <InputNumber min={0} minLength={3} maxLength={8} />
          </Form.Item>

          <Form.Item
            rules={[
              { required: true, message: "required" },
              ({ getFieldValue }) => ({
                validator(_, value, callback) {
                  if (value !== undefined && value <= getFieldValue("startUserId")) {
                    callback("Invalid");
                  }
                  callback();
                }
              })
            ]}
            key={"endUserId"}
            name={"endUserId"}
            label="End Id">
            <InputNumber min={0} minLength={3} maxLength={8} />
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            key={"dialedNumber"}
            name={"dialedNumber"}
            label={"Dialed Number"}>
            <Select
              placeholder="Please select a number"
              fieldNames={{ label: "tollFreeNumber", value: "dialedNumber" }}
              options={dialedNumbersMap}
              defaultActiveFirstOption={true}
            />
          </Form.Item>

          <Form.Item label="&nbsp;">
            <Button type="primary" htmlType="submit" className="primary-btn">
              Search
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {lookedup && (
        <Card className="default-border">
          {!availableUserIds?.length && (
            <Empty description={"No available User Ids for the selected range"} />
          )}
          {availableUserIds?.length > 0 && (
            <Flex wrap gap="4px 0" style={{ maxHeight: "300px", overflowY: "auto" }}>
              {availableUserIds.map(id => (
                <Tag
                  key={id}
                  className="vt-available-user-id-tag"
                  onClick={() => onClose(id, selectedDialedNumber)}>
                  {id}
                </Tag>
              ))}
            </Flex>
          )}
        </Card>
      )}
    </Modal>
  );
};

export default SearchAvailableVTUsers;
